import type { BreadcrumbItem } from 'src/UI/app-container/BreadcrumbTrail';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import type { ReviewOrigin } from './reviewOrigin';

type Person = { firstName?: string | null; lastName?: string | null };

type ReviewedClient = Person & { id: string };

type TrailNames = {
  meetingTitle?: string;
  fileName?: string;
  /** Owns the documents the review was opened from, when that isn't the reviewed client. */
  otherClient?: Person | null;
  householdSurname?: string | null;
};

type ReviewTrail = { items: BreadcrumbItem[]; endsAtCurrentPage: boolean };

// Names the client whose data is being processed, even for a review opened from their household
// or another member's documents, and then says whose documents those are.
export function reviewCrumbs(
  client: ReviewedClient,
  origin: ReviewOrigin,
  names: TrailNames,
  currentUrl: string,
): ReviewTrail {
  const items: BreadcrumbItem[] = [
    { label: 'All clients', href: '/clients/all' },
    {
      label: formatName(client.firstName, client.lastName, NameFormat.NATURAL),
      href: `/client/${client.id}/non-financial-data`,
      highlight: true,
    },
  ];

  if (origin.kind === 'meeting') {
    items.push({ label: names.meetingTitle || 'Meeting', href: origin.href });
    return { items, endsAtCurrentPage: false };
  }

  items.push({ label: documentsLabel(client, origin, names), href: origin.href });
  if (!names.fileName) {
    return { items, endsAtCurrentPage: false };
  }
  items.push({ label: names.fileName, href: currentUrl });
  return { items, endsAtCurrentPage: true };
}

function documentsLabel(
  client: ReviewedClient,
  origin: Exclude<ReviewOrigin, { kind: 'meeting' }>,
  names: TrailNames,
): string {
  if (origin.kind === 'household-documents') {
    return names.householdSurname
      ? `${names.householdSurname} household documents`
      : 'Household documents';
  }
  if (origin.clientId === client.id) {
    return 'Documents';
  }
  const owner = formatName(
    names.otherClient?.firstName,
    names.otherClient?.lastName,
    NameFormat.NATURAL,
  );
  return owner ? `${owner}'s documents` : 'Documents';
}
